import { MIN_SPAN, panWindow, spanOf, type TimeWindow } from "./window";

/**
 * What a press on the overview strip does as the pointer moves: `move` drags
 * the whole mark, `start`/`end` drag that edge, and `select` draws a new span
 * from the press on empty strip.
 */
export type BrushMode = "move" | "start" | "end" | "select";

/**
 * The instant at `fraction` of the way across `extent`. Not clamped: a
 * handle's grab area reaches past the strip's ends, and a drag measured
 * from there must keep its true distance.
 */
export function timeAtFraction(extent: TimeWindow, fraction: number): number {
  return extent.start + fraction * spanOf(extent);
}

/** `ms` moved inside `extent`, so a span drawn past the strip's ends stops at them. */
export function clampTime(extent: TimeWindow, ms: number): number {
  return Math.min(Math.max(ms, extent.start), extent.end);
}

/**
 * The window a brush drag asks for, given the window when the press began
 * (`from`), the instant under the press (`anchorMs`), and the instant under the
 * pointer now (`pointerMs`).
 *
 * - `move` shifts `from` by the drag distance, keeping its span.
 * - `start`/`end` shifts that edge by the drag distance, so the edge keeps
 *   its place under the pointer wherever in the grab area the press began,
 *   stopping `MIN_SPAN` short of the other edge so the edges never cross.
 * - `select` spans the press and the pointer, in either drag direction.
 */
export function brushWindow(
  mode: BrushMode,
  from: TimeWindow,
  anchorMs: number,
  pointerMs: number,
): TimeWindow {
  switch (mode) {
    case "move":
      return panWindow(from, pointerMs - anchorMs);
    case "start":
      return {
        start: Math.min(from.start + pointerMs - anchorMs, from.end - MIN_SPAN),
        end: from.end,
      };
    case "end":
      return {
        start: from.start,
        end: Math.max(from.end + pointerMs - anchorMs, from.start + MIN_SPAN),
      };
    case "select":
      return {
        start: Math.min(anchorMs, pointerMs),
        end: Math.max(anchorMs, pointerMs),
      };
  }
}
